import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Card } from '@treams/treams-ui/common/Card';
import { Empty } from '@treams/treams-ui/common/Empty';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { Heading3, Paragraph } from '@treams/treams-ui/common/Text';
import { CashIcon } from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import { SalaryRangeBar } from './SalaryRangeBar';
import { type RewardEmployee, type RewardFunction } from './buildRewardFunctions';

// Average full-time salary of everyone with a salary; for one person, their own.
function SalaryLabel({ employees }: { employees: RewardEmployee[] }) {
	const { i18n } = useTranslation();
	const salaries = employees.flatMap((employee) =>
		employee.salary == null ? [] : [employee.salary],
	);

	if (salaries.length === 0) {
		return null;
	}

	const average = salaries.reduce((sum, salary) => sum + salary, 0) / salaries.length;

	return <Paragraph small>{formatSalary(Math.round(average), i18n.language)}</Paragraph>;
}

function RangeColumn({
	label,
	range,
	employees,
	emptyLabel,
}: {
	label: string;
	range?: RewardFunction['range'];
	employees: RewardEmployee[];
	emptyLabel: string;
}) {
	const people = employees.flatMap((employee) =>
		employee.salary == null ? [] : [{ ...employee, salary: employee.salary }],
	);

	return (
		<Box flex="2 1 220px" minWidth={0}>
			{people.length > 0 ? (
				<SalaryRangeBar label={label} range={range} people={people} />
			) : (
				<Paragraph small>{emptyLabel}</Paragraph>
			)}
		</Box>
	);
}

export function RewardFunctions({ functions }: { functions: RewardFunction[] }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState<Record<string, boolean>>({});

	if (functions.length === 0) {
		return (
			<Empty.Card
				icon={<CashIcon />}
				title={t('analytics.reward.empty.title')}
				description={t('analytics.reward.empty.description')}
			/>
		);
	}

	return (
		<Card>
			<Heading3>{t('analytics.reward.title')}</Heading3>
			<FlexGap mt={3} mb={2} alignItems="center" gap={tokens.gap.list}>
				<Box width={16} flexShrink={0} />
				<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
					<Paragraph small>{t('analytics.reward.genderGap.column.function')}</Paragraph>
				</Box>
				<Box flex="2 1 220px" minWidth={0}>
					<Paragraph small>{t('analytics.reward.column.range')}</Paragraph>
				</Box>
			</FlexGap>
			<Box>
				{functions.map((row) => {
					const expanded = open[row.id] ?? false;

					return (
						<Accordion
							key={row.id}
							borderless
							noPadding
							isExpanded={expanded}
							onClick={() => setOpen((current) => ({ ...current, [row.id]: !expanded }))}
							customHeader={
								<FlexGap
									alignItems="center"
									gap={tokens.gap.list}
									style={{
										paddingTop: tokens.padding.control,
										paddingBottom: tokens.padding.control,
									}}
								>
									<Box
										style={{
											display: 'flex',
											flexShrink: 0,
											color: tokens.icon.tertiary,
											transform: expanded ? 'rotate(90deg)' : undefined,
											transition: 'transform 120ms ease-out',
										}}
									>
										<ChevronRightIcon size={16} />
									</Box>
									<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
										<Heading3>{row.name}</Heading3>
										<SalaryLabel employees={row.employees} />
									</Box>
									<RangeColumn
										label={row.name}
										range={row.range}
										employees={row.employees}
										emptyLabel={t('analytics.reward.noSalary')}
									/>
								</FlexGap>
							}
						>
							<Box
								style={{
									paddingLeft: tokens.gap.section,
									paddingBottom: tokens.padding.container,
								}}
							>
								{row.employees.map((employee) => (
									<FlexGap
										key={employee.id}
										alignItems="center"
										gap={tokens.gap.list}
										py={`${tokens.padding.control}px`}
									>
										<Box width={16} flexShrink={0} />
										<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
											<Paragraph>{employee.name}</Paragraph>
											<SalaryLabel employees={[employee]} />
										</Box>
										<RangeColumn
											label={employee.name}
											range={employee.range}
											employees={[employee]}
											emptyLabel={t('analytics.reward.noSalary')}
										/>
									</FlexGap>
								))}
							</Box>
						</Accordion>
					);
				})}
			</Box>
		</Card>
	);
}

RewardFunctions.Skeleton = function RewardFunctionsSkeleton() {
	return (
		<Card>
			<Heading3.Skeleton width={160} />
			<Box mt={3}>
				<Accordion.Skeleton borderless />
				<Accordion.Skeleton borderless />
				<Accordion.Skeleton borderless />
			</Box>
		</Card>
	);
};
